import { LinkToFunc } from 'src/components/app-context';
import { TranslateFunc } from 'src/components/translate';
import { ClientMarket } from 'config/types/client-market';
import { MarketConfig } from '@prisjakt/web-app-config/dist/markets/types';
import { PrismicArticle } from 'src/types';
import { Page, Pages } from '../../globals/actions/types';
import { Column } from './types';

const ocastLink = {
  no: 'https://no-prisjakt.ocast.com/no/',
  se: 'https://se-prisjakt.ocast.com/se/',
  fi: 'https://hintaopas.ocast.com/fi/',
  gb: 'https://uk-pricespy.ocast.com/',
  nz: 'https://nz-pricespy.ocast.com/',
};

const careersLink = {
  se: 'https://prisjakt.teamtailor.com/',
};

const expertLoginLink = {
  se: 'http://expert.prisjakt.nu/login',
};

const newsroomLink = {
  no: 'https://nyhetsrom.prisjakt.no',
  se: 'https://nyhetsrum.prisjakt.nu',
  fi: 'https://uutishuone.hintaopas.fi',
  fr: 'https://newsroom.ledenicheur.fr ',
  gb: 'https://newsroom.pricespy.co.uk',
  nz: 'https://newsroom.pricespy.co.nz',
};

type PageMapKey =
  | 'themes'
  | 'advertiseOnPrisjakt'
  | 'careers'
  | 'articles'
  | 'shopOfTheYear'
  | 'categoryIndex'
  | 'privacySettings'
  | 'supportPage'
  | 'expertLogin'
  | 'experts'
  | 'newsroom'
  | 'blackFriday'
  | keyof NonNullable<ClientMarket['infoPageMap']>;

export const getPageMap = (
  infoPageMap: ClientMarket['infoPageMap'],
  url: MarketConfig['url'] | undefined,
  market: ClientMarket,
  linkTo: LinkToFunc,
  t: TranslateFunc,
  pages?: Pages
) => {
  const pageMap = {
    themes: !!url?.campaigns && {
      pathName: url?.campaigns || '',
      title: t('campaigns', 'themes.title'),
      id: 'themes',
      follow: true,
    },
    advertiseOnPrisjakt: ocastLink[market.code] && {
      pathName: ocastLink[market.code],
      title: t('footer', 'link.ads'),
      id: 'adlink',
      follow: false,
    },
    careers: careersLink[market.code] && {
      pathName: careersLink[market.code],
      title: t('footer', 'link.careers'),
      id: 'careers',
      follow: false,
    },
    articles: !!infoPageMap?.articles && {
      pathName: linkTo('editorialContentListing'),
      title: t('common', 'article.articles'),
      id: 'articles',
      follow: true,
    },
    shopOfTheYear: !!infoPageMap?.shopOfTheYear && {
      pathName: linkTo('shopOfTheYear'),
      title: t('campaigns', 'shop-of-the-year.title'),
      id: 'shopOfTheYear',
      follow: true,
    },
    categoryIndex: !!infoPageMap?.categoryIndex && {
      pathName: linkTo('categoryIndex'),
      title: t('category', 'category-index.categories'),
      id: 'categoryIndex',
      follow: true,
    },
    privacySettings: !!infoPageMap?.privacySettings && {
      pathName: market.privacySettings?.responsive?.privacySettingsUrl || '',
      title: t('tracking-settings', 'link.privacy-settings'),
      id: 'privacySettings',
      follow: false,
    },
    supportPage: !!infoPageMap?.supportPage && {
      pathName: `https://support.prisjakt.nu/${
        market && market.locale
          ? `${market.locale.split('_')[0].toLowerCase()}/`
          : 'en/'
      }`,
      title: t('footer', 'contact-helpcenter.link'),
      id: 'supportPage',
      follow: false,
    },
    expertLogin: expertLoginLink[market.code] && {
      pathName: expertLoginLink[market.code],
      title: t('footer', 'link.expert-login'),
      id: 'expertLogin',
      follow: false,
    },
    experts: !!infoPageMap?.experts && {
      pathName: linkTo('experts'),
      title: t('footer', 'link.experts'),
      id: 'experts',
      follow: true,
    },
    newsroom: newsroomLink[market.code] && {
      pathName: newsroomLink[market.code],
      title: t('footer', 'link.newsroom'),
      id: 'newsroom',
      follow: true,
    },
    blackFriday: !!infoPageMap?.blackFriday && {
      pathName: `${url?.campaigns}/black-friday`,
      title: `Black Friday ${new Date().getFullYear()}`,
      id: 'blackFriday',
      follow: true,
    },
  } as Record<PageMapKey, Page>;

  // Add `pages` to `pageMap` with their IDs as keys
  if (pages && infoPageMap) {
    pages.nodes.forEach(page => {
      const name = Object.keys(infoPageMap).find(
        infoPageName => page.id === infoPageMap[infoPageName]
      );
      const id = name && infoPageMap[name];
      if (id) {
        pageMap[name] = page;
      }
    });
  }

  return pageMap;
};

export const getFooterColumns = (
  pageMap: Record<PageMapKey, Page>,
  t: TranslateFunc
): Column[] => {
  return [
    {
      title: t('footer', 'pages.title'),
      pages: [
        pageMap.about,
        pageMap.priceInformation,
        pageMap.aboutReviews,
        pageMap.contact,
        pageMap.registerShop,
        pageMap.adlink,
        pageMap.supportPage,
      ],
    },
    {
      title: t('footer', 'pages.rules-title'),
      pages: [
        pageMap.userAgreement,
        pageMap.disclaimer,
        pageMap.privacyPolicy,
        pageMap.cookiePolicy,
        pageMap.privacySettings,
      ],
    },
    {
      title: t('footer', 'pages.on-title'),
      pages: [
        // Do not open before BF campaign (don't forget the link in the new footer columns below)
        // pageMap.blackFriday,
        pageMap.careers,
        pageMap.themes,
        pageMap.newsroom,
        pageMap.articles,
        pageMap.shopOfTheYear,
        pageMap.categoryIndex,
        pageMap.experts,
        pageMap.expertLogin,
      ],
    },
  ];
};

export const getNewFooterColumns = (
  pageMap: Record<PageMapKey, Page>,
  t: TranslateFunc,
  prismicArticles: PrismicArticle[] | undefined
) => {
  const titleMap = {
    'friskrivning-och-ansvarsbegransning':
      'Friskrivning och ansvarsbegränsning',
    personuppgiftspolicy: 'Personuppgiftspolicy',
    cookiepolicy: 'Cookiepolicy',
    'kom-igang-med-prisjakt': 'Kom igång med Prisjakt',
    'prisjakt-for-butiker': 'Varför Prisjakt?',
    'lagg-till-och-profilera-din-butik': 'Lägg till och profilera din butik',
    'transparenslofte-hur-prisjakt-fungerar': 'Hur Prisjakt fungerar',
    'kontakta-oss': 'Kontakt',
    'arets-butik-for-butiker': 'Årets butik – för butiker',
  } as const;

  type ArticleMap = Record<keyof typeof titleMap, Page>;

  const formatArticle = (article: PrismicArticle) => ({
    pathName: article.pathName,
    title: titleMap[article.uid || ''],
    id: article.id,
    follow: false,
  });

  const prismicPageMap: ArticleMap = (prismicArticles || [])?.reduce(
    (tot, currentArticle) => {
      for (const mappedUid of Object.keys(titleMap)) {
        if (currentArticle.uid === mappedUid) {
          return {
            ...tot,
            [currentArticle.uid]: formatArticle(currentArticle),
          };
        }
      }

      return tot;
    },
    {} as ArticleMap
  );

  return [
    {
      title: t('footer', 'column.consumer-title'),
      pages: [
        prismicPageMap['kom-igang-med-prisjakt'],
        prismicPageMap['transparenslofte-hur-prisjakt-fungerar'],
        pageMap.articles,
        pageMap.experts,
        pageMap.themes,
        // pageMap.blackFriday,
        pageMap.shopOfTheYear,
        pageMap.categoryIndex,
      ],
    },
    {
      title: t('footer', 'column.store-title'),
      pages: [
        prismicPageMap['prisjakt-for-butiker'],
        prismicPageMap['lagg-till-och-profilera-din-butik'],
        pageMap.advertiseOnPrisjakt,
        prismicPageMap['arets-butik-for-butiker'],
        {
          pathName: 'https://business.prisjakt.nu',
          title: 'Login för butiker',
          id: 'login-for-butiker',
          follow: false,
        },
      ],
    },
    {
      title: t('footer', 'column.about-us-title'),
      pages: [
        pageMap.about,
        pageMap.supportPage,
        prismicPageMap['kontakta-oss'],
        pageMap.careers,
        pageMap.newsroom,
        prismicPageMap['friskrivning-och-ansvarsbegransning'],
        prismicPageMap.personuppgiftspolicy,
        prismicPageMap.cookiepolicy,
        pageMap.expertLogin,
      ],
    },
  ];
};
